import { useQueryClient } from '@tanstack/react-query';
import {
  CrmFunnelsStatusesService,
  type FunnelStatus,
  type FunnelConversation,
} from 'src/api/generated';
import { useDeferredAction } from 'src/modules/shared/hooks/provider-bounded/deferred-action';
import { getModelFromCache } from 'src/modules/shared/utils/cache/update.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

/** Defers user archive intent without writing optimistic or server state until expiry. */
export const useConversationArchiveUndo = () => {
  const deferred = useDeferredAction();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  return async (
    ids: number[],
    data: Partial<FunnelConversation>,
    focusUndo = false
  ): Promise<boolean> => {
    if (!deferred) {
      throw new Error(
        'DeferredActionProvider is required for user conversation edits'
      );
    }
    if (data.funnelStatusId === undefined && data.archivedAt === undefined) {
      return true;
    }
    const resourceKeys = ids.map((id) => `conversation:${id}`);
    const intents = resourceKeys.map((key) => deferred.start(key));
    const isCurrent = () => intents.every((intent) => intent.isCurrent());
    try {
      const statusId = data.funnelStatusId;
      const status =
        statusId == null
          ? undefined
          : getModelFromCache<FunnelStatus>(
              queryClient,
              ['crm/funnels/statuses'],
              statusId
            ) ?? (await CrmFunnelsStatusesService.getFunnelStatus(statusId));
      if (!isCurrent()) return false;
      if (!status?.terminalOutcome && !data.archivedAt) return true;
      const commit = await deferred.wait({
        focusUndo,
        key: resourceKeys.join(','),
        resourceKeys,
        message:
          ids.length === 1
            ? t('communications.conversation.archivePending', { id: ids[0] })
            : t('communications.conversation.bulkArchivePending', {
                count: ids.length,
              }),
        undoLabel: t('common.undo'),
      });
      return commit && isCurrent();
    } finally {
      intents.forEach((intent) => intent.release());
    }
  };
};
